import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthAgentService, GrowthStrategyArtifact } from './growth-agent.service';
import {
  buildMergedRichArtifactJson,
  detectRichStrategyArtifactKind,
  EditableMarketingPlanModel,
  EditablePositioningModel,
  EditableSituationModel,
  EditableSwotFactor,
  EditableSwotModel,
  hydrateEditableMarketingPlanModel,
  hydrateEditablePositioningModel,
  hydrateEditableSituationModel,
  hydrateEditableSwotModel,
  isMarketingPlanArtifactType,
  isPositioningArtifactType,
  isSituationAssessmentArtifactType,
  isSwotArtifactType,
  parseRichMarketingPlanPayload,
  parseRichPositioningPayload,
  parseRichSituationAssessmentPayload,
  parseRichSwotPayload,
  parseStrategyArtifactJson,
  RichStrategyArtifactKind,
  RichSwotPayload,
  SWOT_FACTOR_TYPES,
  swotFactorTypeI18nKey,
} from './growth-strategy-artifact-content.util';
import {
  BRAND_VOICE_TONE_PRESET_OPTIONS,
  normalizeStrategyArtifactStatus,
  normalizeStrategyArtifactType,
  STRATEGY_ARTIFACT_TYPE_OPTIONS,
} from './growth-status.util';

interface StrategyArtifactFormModel {
  artifactType: string;
  title: string;
  objective: string;
  targetSegment: string;
  budgetRange: string;
  timeline: string;
  kpis: string;
  channels: string;
  campaigns: string;
  messaging: string;
  successMetrics: string;
  linkedBusinessPlanId: string | null;
  strengths: string;
  weaknesses: string;
  opportunities: string;
  threats: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-strategy-artifact-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    FormsModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    TableModule,
    RouterLink
  ],
  template: `
    <ain-page [eyebrow]="tr('growth.eyebrow', 'رشد')" [title]="title()" [description]="tr('growth.strategy.description', 'آرتیفکت استراتژی را مدیریت کنید.')">
      <div class="toolbar">
        <a pButton routerLink="/growth/agents/strategy-artifacts" severity="secondary" [label]="tr('common.back', 'بازگشت')"></a>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ title() }}</h3>
            <ain-info-hint
              [titleKey]="'growth.strategy.dialog.help.title'"
              [descriptionKey]="'growth.strategy.dialog.help.description'"
              [bulletKeys]="[
                'growth.strategy.dialog.help.bullets.swot',
                'growth.strategy.dialog.help.bullets.approval',
                'growth.strategy.dialog.help.bullets.activation'
              ]" />
          </div>

          <div class="grid">
            <ain-form-field [label]="t('growth.strategy.fields.type')">
              <p-select
                [options]="typeOptions()"
                [(ngModel)]="model.artifactType"
                optionLabel="label"
                optionValue="value"
                [disabled]="!isNew() || isRichView()"
                appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.fields.title')">
              <input pInputText class="text-input" [(ngModel)]="model.title" />
            </ain-form-field>
          </div>

          @if (isRichView()) {
            @if (richKind === 'swot' && editSwot; as swot) {
              <div class="rich-view">
                @if (richSwot(); as scores) {
                  <div class="meta-row">
                    @if (scores.internalScore !== undefined) {
                      <span>{{ t('growth.strategy.fields.internalScore') }}: {{ scores.internalScore }}</span>
                    }
                    @if (scores.externalScore !== undefined) {
                      <span>{{ t('growth.strategy.fields.externalScore') }}: {{ scores.externalScore }}</span>
                    }
                  </div>
                }
                <ain-form-field [label]="t('growth.strategy.fields.strategyLabel')">
                  <input pInputText class="text-input" [(ngModel)]="swot.strategyLabel" />
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.fields.situationSummary')">
                  <textarea pInputTextarea rows="4" [(ngModel)]="swot.situationSummary"></textarea>
                </ain-form-field>
                <div class="list-editor">
                  <div class="list-header">
                    <h4>{{ t('growth.strategy.fields.keyInsights') }}</h4>
                    <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addInsight')" (click)="addStringItem(swot.keyInsights)"></button>
                  </div>
                  @for (item of swot.keyInsights; track $index) {
                    <div class="list-row">
                      <input pInputText class="text-input" [(ngModel)]="swot.keyInsights[$index]" />
                      <button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeStringItem(swot.keyInsights, $index)"></button>
                    </div>
                  }
                </div>
                <div class="matrix-grid">
                  @for (factorType of swotFactorTypes; track factorType) {
                    <div class="quadrant-block">
                      <div class="list-header">
                        <h4>{{ t(swotFactorTypeKey(factorType)) }}</h4>
                        <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addFactor')" (click)="addSwotFactor(factorType)"></button>
                      </div>
                      @for (factor of editSwotFactorsByType(factorType); track factor) {
                        <div class="factor-edit-card">
                          <ain-form-field [label]="t('growth.journey.swot.matrix.factor')">
                            <input pInputText class="text-input" [(ngModel)]="factor.title" />
                          </ain-form-field>
                          <ain-form-field [label]="t('growth.journey.marketingPlan.fields.description')">
                            <textarea pInputTextarea rows="2" [(ngModel)]="factor.description"></textarea>
                          </ain-form-field>
                          <div class="factor-metrics">
                            <ain-form-field [label]="t('growth.strategy.fields.factorRank')">
                              <p-inputNumber [(ngModel)]="factor.rank" [min]="0" [max]="4" />
                            </ain-form-field>
                            <ain-form-field [label]="t('growth.strategy.fields.factorWeight')">
                              <p-inputNumber [(ngModel)]="factor.relativeWeight" [min]="0" [max]="1" [step]="0.05" />
                            </ain-form-field>
                          </div>
                          <button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeSwotFactor(factor)"></button>
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            } @else if (richKind === 'situationAssessment' && editSituation; as situation) {
              <div class="rich-view">
                <ain-form-field [label]="t('growth.strategy.fields.strategyLabel')">
                  <input pInputText class="text-input" [(ngModel)]="situation.strategyLabel" />
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.situation.marketContext')">
                  <textarea pInputTextarea rows="3" [(ngModel)]="situation.marketContext"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.situation.competitiveLandscape')">
                  <textarea pInputTextarea rows="3" [(ngModel)]="situation.competitiveLandscape"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.situation.customerSegmentsInsight')">
                  <textarea pInputTextarea rows="3" [(ngModel)]="situation.customerSegmentsInsight"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.situation.channelReadiness')">
                  <textarea pInputTextarea rows="3" [(ngModel)]="situation.channelReadiness"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.situation.opportunityHorizon')">
                  <textarea pInputTextarea rows="3" [(ngModel)]="situation.opportunityHorizon"></textarea>
                </ain-form-field>
                <div class="list-editor">
                  <div class="list-header">
                    <h4>{{ t('growth.strategy.situation.riskFlags') }}</h4>
                    <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addRisk')" (click)="addStringItem(situation.riskFlags)"></button>
                  </div>
                  @for (item of situation.riskFlags; track $index) {
                    <div class="list-row">
                      <input pInputText class="text-input" [(ngModel)]="situation.riskFlags[$index]" />
                      <button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeStringItem(situation.riskFlags, $index)"></button>
                    </div>
                  }
                </div>
              </div>
            } @else if (richKind === 'positioning' && editPositioning; as positioning) {
              <div class="rich-view">
                <ain-form-field [label]="t('growth.strategy.positioning.statement')">
                  <textarea pInputTextarea rows="3" [(ngModel)]="positioning.positioningStatement"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.positioning.targetAudience')">
                  <textarea pInputTextarea rows="2" [(ngModel)]="positioning.targetAudience"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.positioning.frameOfReference')">
                  <textarea pInputTextarea rows="2" [(ngModel)]="positioning.frameOfReference"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.positioning.pointOfDifference')">
                  <textarea pInputTextarea rows="2" [(ngModel)]="positioning.pointOfDifference"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.positioning.reasonToBelieve')">
                  <textarea pInputTextarea rows="2" [(ngModel)]="positioning.reasonToBelieve"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.positioning.elevatorPitch')">
                  <textarea pInputTextarea rows="2" [(ngModel)]="positioning.elevatorPitch"></textarea>
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.positioning.valueProposition')">
                  <textarea pInputTextarea rows="2" [(ngModel)]="positioning.valueProposition"></textarea>
                </ain-form-field>
                <div class="list-editor">
                  <div class="list-header">
                    <h4>{{ t('growth.strategy.positioning.differentiators') }}</h4>
                    <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addDifferentiator')" (click)="addStringItem(positioning.differentiators)"></button>
                  </div>
                  @for (item of positioning.differentiators; track $index) {
                    <div class="list-row">
                      <input pInputText class="text-input" [(ngModel)]="positioning.differentiators[$index]" />
                      <button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeStringItem(positioning.differentiators, $index)"></button>
                    </div>
                  }
                </div>
                <div class="list-editor">
                  <div class="list-header">
                    <h4>{{ t('growth.strategy.positioning.messagingPillars') }}</h4>
                    <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addPillar')" (click)="addPositioningPillar()"></button>
                  </div>
                  @for (pillar of positioning.messagingPillars; track $index) {
                    <div class="factor-edit-card">
                      <ain-form-field [label]="t('growth.journey.marketingPlan.fields.pillarTitle')">
                        <input pInputText class="text-input" [(ngModel)]="pillar.title" />
                      </ain-form-field>
                      <ain-form-field [label]="t('growth.strategy.positioning.messagingPillars')">
                        <textarea pInputTextarea rows="2" [(ngModel)]="pillar.message"></textarea>
                      </ain-form-field>
                      <div class="list-editor nested">
                        <div class="list-header">
                          <span class="meta-label">{{ t('growth.strategy.positioning.supportingPoints') }}</span>
                          <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addPoint')" (click)="addStringItem(pillar.supportingPoints)"></button>
                        </div>
                        @for (point of pillar.supportingPoints; track $index) {
                          <div class="list-row">
                            <input pInputText class="text-input" [(ngModel)]="pillar.supportingPoints[$index]" />
                            <button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeStringItem(pillar.supportingPoints, $index)"></button>
                          </div>
                        }
                      </div>
                      <button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removePositioningPillar($index)"></button>
                    </div>
                  }
                </div>
                <ain-form-field [label]="t('growth.strategy.positioning.recommendedTonePreset')">
                  <p-select
                    [options]="tonePresetOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [(ngModel)]="positioning.recommendedTonePreset"
                    appendTo="body" />
                </ain-form-field>
              </div>
            } @else if (richKind === 'marketingPlan' && editMarketingPlan; as plan) {
              <div class="rich-view">
                <ain-form-field [label]="t('growth.strategy.marketingPlan.planningHorizonDays')">
                  <p-inputNumber [(ngModel)]="plan.planningHorizonDays" [min]="1" />
                </ain-form-field>
                <div class="list-header">
                  <h4>{{ t('growth.strategy.marketingPlan.goals') }}</h4>
                  <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addGoal')" (click)="addMarketingGoal()"></button>
                </div>
                <p-table [value]="plan.goals">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.goalTitle') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.measurable') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.timeBound') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                    <tr>
                      <td><input pInputText class="text-input" [(ngModel)]="row.title" /></td>
                      <td><input pInputText class="text-input" [(ngModel)]="row.measurable" /></td>
                      <td><input pInputText class="text-input" [(ngModel)]="row.timeBound" /></td>
                      <td><button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeMarketingGoal(rowIndex)"></button></td>
                    </tr>
                  </ng-template>
                </p-table>
                <div class="list-header">
                  <h4>{{ t('growth.strategy.marketingPlan.kpis') }}</h4>
                  <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addKpi')" (click)="addMarketingKpi()"></button>
                </div>
                <p-table [value]="plan.kpis">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.kpiName') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.metricKey') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.targetValue') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.unit') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                    <tr>
                      <td><input pInputText class="text-input" [(ngModel)]="row.name" /></td>
                      <td><input pInputText class="text-input" [(ngModel)]="row.metricKey" /></td>
                      <td><input pInputText class="text-input" [(ngModel)]="row.targetValue" /></td>
                      <td><input pInputText class="text-input" [(ngModel)]="row.unit" /></td>
                      <td><button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeMarketingKpi(rowIndex)"></button></td>
                    </tr>
                  </ng-template>
                </p-table>
                <div class="list-header">
                  <h4>{{ t('growth.strategy.marketingPlan.segments') }}</h4>
                  <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addSegment')" (click)="addMarketingSegment()"></button>
                </div>
                <p-table [value]="plan.segments">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.segmentName') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.description') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                    <tr>
                      <td><input pInputText class="text-input" [(ngModel)]="row.name" /></td>
                      <td><input pInputText class="text-input" [(ngModel)]="row.description" /></td>
                      <td><button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeMarketingSegment(rowIndex)"></button></td>
                    </tr>
                  </ng-template>
                </p-table>
                <div class="list-header">
                  <h4>{{ t('growth.strategy.marketingPlan.channelMix') }}</h4>
                  <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addChannel')" (click)="addMarketingChannel()"></button>
                </div>
                <p-table [value]="plan.channelMix">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.channelKey') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.weight') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.rationale') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                    <tr>
                      <td><input pInputText class="text-input" [(ngModel)]="row.channelKey" /></td>
                      <td><p-inputNumber [(ngModel)]="row.weight" [min]="0" [max]="1" [step]="0.05" /></td>
                      <td><input pInputText class="text-input" [(ngModel)]="row.rationale" /></td>
                      <td><button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeMarketingChannel(rowIndex)"></button></td>
                    </tr>
                  </ng-template>
                </p-table>
                <div class="list-header">
                  <h4>{{ t('growth.strategy.marketingPlan.contentPillars') }}</h4>
                  <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addContentPillar')" (click)="addMarketingContentPillar()"></button>
                </div>
                <p-table [value]="plan.contentPillars">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.pillarTitle') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.themes') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                    <tr>
                      <td><input pInputText class="text-input" [(ngModel)]="row.title" /></td>
                      <td><input pInputText class="text-input" [(ngModel)]="row.themesJoined" [placeholder]="t('growth.journey.marketingPlan.fields.themes')" /></td>
                      <td><button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeMarketingContentPillar(rowIndex)"></button></td>
                    </tr>
                  </ng-template>
                </p-table>
                <div class="list-header">
                  <h4>{{ t('growth.strategy.marketingPlan.timeline') }}</h4>
                  <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addTimelineWeek')" (click)="addMarketingTimelineWeek()"></button>
                </div>
                <p-table [value]="plan.timeline">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.journey.marketingPlan.fields.week') }}</th>
                      <th>{{ t('growth.journey.marketingPlan.fields.milestones') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                    <tr>
                      <td><p-inputNumber [(ngModel)]="row.week" [min]="1" /></td>
                      <td><input pInputText class="text-input" [(ngModel)]="row.milestonesJoined" [placeholder]="t('growth.journey.marketingPlan.fields.milestones')" /></td>
                      <td><button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeMarketingTimelineWeek(rowIndex)"></button></td>
                    </tr>
                  </ng-template>
                </p-table>
                <div class="list-editor">
                  <div class="list-header">
                    <h4>{{ t('growth.strategy.marketingPlan.risks') }}</h4>
                    <button pButton type="button" severity="secondary" size="small" [label]="t('growth.strategy.actions.addRisk')" (click)="addStringItem(plan.risks)"></button>
                  </div>
                  @for (item of plan.risks; track $index) {
                    <div class="list-row">
                      <input pInputText class="text-input" [(ngModel)]="plan.risks[$index]" />
                      <button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeStringItem(plan.risks, $index)"></button>
                    </div>
                  }
                </div>
              </div>
            }
          } @else {
            <div class="grid">
              @if (isBusinessPlanType()) {
                <ain-form-field [label]="t('growth.strategy.businessPlan.fields.objective')"><textarea pInputTextarea rows="3" [(ngModel)]="model.objective"></textarea></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.businessPlan.fields.targetSegment')"><input pInputText [(ngModel)]="model.targetSegment" /></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.businessPlan.fields.budgetRange')"><input pInputText [(ngModel)]="model.budgetRange" /></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.businessPlan.fields.timeline')"><input pInputText [(ngModel)]="model.timeline" /></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.businessPlan.fields.kpis')"><textarea pInputTextarea rows="3" [(ngModel)]="model.kpis"></textarea></ain-form-field>
              } @else if (isMarketingPlanType()) {
                <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.businessPlan')">
                  <p-select [options]="businessPlanOptions()" optionLabel="label" optionValue="value" [(ngModel)]="model.linkedBusinessPlanId" [showClear]="true" appendTo="body" />
                </ain-form-field>
                <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.channels')"><textarea pInputTextarea rows="3" [(ngModel)]="model.channels"></textarea></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.campaigns')"><textarea pInputTextarea rows="3" [(ngModel)]="model.campaigns"></textarea></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.messaging')"><textarea pInputTextarea rows="3" [(ngModel)]="model.messaging"></textarea></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.successMetrics')"><textarea pInputTextarea rows="3" [(ngModel)]="model.successMetrics"></textarea></ain-form-field>
              } @else {
                <ain-form-field [label]="t('growth.strategy.fields.strengths')"><textarea pInputTextarea rows="4" [(ngModel)]="model.strengths"></textarea></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.fields.weaknesses')"><textarea pInputTextarea rows="4" [(ngModel)]="model.weaknesses"></textarea></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.fields.opportunities')"><textarea pInputTextarea rows="4" [(ngModel)]="model.opportunities"></textarea></ain-form-field>
                <ain-form-field [label]="t('growth.strategy.fields.threats')"><textarea pInputTextarea rows="4" [(ngModel)]="model.threats"></textarea></ain-form-field>
              }
            </div>
          }

          <div class="actions">
            <button pButton type="button" [label]="isNew() ? t('common.create') : t('common.save')" [disabled]="!canSave()" (click)="save()"></button>
            @if (!isNew() && item()) {
              @if (isDraftStatus(item()!.status)) {
                <button pButton type="button" [label]="t('growth.strategy.actions.submit')" (click)="submit()"></button>
              }
              @if (isInReviewStatus(item()!.status)) {
                <button pButton type="button" [label]="t('growth.strategy.actions.approve')" (click)="approve()"></button>
              }
            }
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .text-input { width: 100%; }
    .rich-view { display: grid; gap: 12px; }
    .meta-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .meta-label { color: #6b7280; font-weight: 600; }
    .text-block h4 { margin: 0 0 6px; font-size: 0.95rem; }
    .text-block p { margin: 0; white-space: pre-wrap; }
    .matrix-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 12px; }
    .quadrant-block { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; }
    .quadrant-block h4 { margin: 0 0 8px; font-size: 0.95rem; }
    .factor-list { margin: 0; padding-inline-start: 1.2rem; display: grid; gap: 8px; }
    .factor-desc { margin: 4px 0 0; color: #4b5563; white-space: pre-wrap; }
    .factor-meta { display: block; font-size: 0.85rem; color: #6b7280; margin-top: 4px; }
    .preview-meta { margin: 0; }
    .rich-view h4 { margin: 8px 0 4px; font-size: 0.95rem; }
    .list-editor { display: grid; gap: 8px; }
    .list-editor.nested { margin-top: 8px; padding: 8px; border: 1px dashed #e5e7eb; border-radius: 6px; }
    .list-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
    .list-header h4 { margin: 0; font-size: 0.95rem; }
    .list-row { display: flex; gap: 8px; align-items: center; }
    .list-row .text-input { flex: 1; }
    .factor-edit-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; display: grid; gap: 8px; margin-bottom: 8px; }
    .factor-metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 8px; }
    :host ::ng-deep .p-inputnumber { width: 100%; }
  `]
})
export class GrowthStrategyArtifactDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(GrowthAgentService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly isNew = signal(false);
  readonly item = signal<GrowthStrategyArtifact | null>(null);
  readonly items = signal<GrowthStrategyArtifact[]>([]);
  readonly isRichView = signal(false);
  readonly richSwot = signal<RichSwotPayload | null>(null);

  readonly swotFactorTypes = SWOT_FACTOR_TYPES;

  richKind: RichStrategyArtifactKind | null = null;
  originalArtifactObj: Record<string, unknown> | null = null;
  originalCitationRefsJson = '[]';
  editSwot: EditableSwotModel | null = null;
  editSituation: EditableSituationModel | null = null;
  editPositioning: EditablePositioningModel | null = null;
  editMarketingPlan: EditableMarketingPlanModel | null = null;

  private editingVersion = '1.0.0';

  model: StrategyArtifactFormModel = this.emptyModel();

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.isNew.set(!id || id === 'new');
    this.service.listStrategyArtifacts().subscribe({
      next: rows => {
        this.items.set(rows);
        if (this.isNew()) {
          this.loading.set(false);
          return;
        }
        const current = rows.find(x => x.id === id);
        if (!current) {
          this.error.set(this.t('common.notFound'));
          this.loading.set(false);
          return;
        }
        this.hydrateArtifact(current);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.error'));
        this.loading.set(false);
      }
    });
  }

  title(): string {
    return this.isNew()
      ? this.tr('growth.strategy.dialog.createTitle', 'ایجاد آرتیفکت استراتژی')
      : this.tr('growth.strategy.dialog.editTitle', 'ویرایش آرتیفکت استراتژی');
  }

  typeOptions() {
    return STRATEGY_ARTIFACT_TYPE_OPTIONS.map(value => ({ value, label: this.t(`growth.strategy.artifactTypes.${value}`) }));
  }

  businessPlanOptions(): Array<{ label: string; value: string }> {
    return this.items()
      .filter(item => normalizeStrategyArtifactType(item.artifactType) === 'BusinessPlan')
      .map(item => ({ label: item.title, value: item.id }));
  }

  isBusinessPlanType(): boolean { return this.model.artifactType === 'BusinessPlan'; }
  isSwotType(): boolean { return isSwotArtifactType(this.model.artifactType); }
  isSituationAssessmentType(): boolean { return isSituationAssessmentArtifactType(this.model.artifactType); }
  isPositioningType(): boolean { return isPositioningArtifactType(this.model.artifactType); }
  isMarketingPlanType(): boolean { return isMarketingPlanArtifactType(this.model.artifactType); }
  isDraftStatus(status: string | number): boolean { return normalizeStrategyArtifactStatus(status) === 'Draft'; }
  isInReviewStatus(status: string | number): boolean { return normalizeStrategyArtifactStatus(status) === 'InReview'; }

  swotFactorTypeKey(type: string): string {
    return swotFactorTypeI18nKey(type);
  }

  editSwotFactorsByType(factorType: string): EditableSwotFactor[] {
    return this.editSwot?.factors.filter(f => f.type === factorType) ?? [];
  }

  tonePresetOptions(): Array<{ label: string; value: number }> {
    return BRAND_VOICE_TONE_PRESET_OPTIONS.map(option => ({
      label: this.t(option.i18nKey),
      value: option.numeric
    }));
  }

  addStringItem(list: string[]): void {
    list.push('');
  }

  removeStringItem(list: string[], index: number): void {
    list.splice(index, 1);
  }

  addSwotFactor(factorType: string): void {
    this.editSwot?.factors.push({
      type: factorType,
      title: '',
      description: '',
      rank: null,
      relativeWeight: null
    });
  }

  removeSwotFactor(factor: EditableSwotFactor): void {
    if (!this.editSwot) {
      return;
    }
    this.editSwot.factors = this.editSwot.factors.filter(item => item !== factor);
  }

  addPositioningPillar(): void {
    this.editPositioning?.messagingPillars.push({ title: '', message: '', supportingPoints: [] });
  }

  removePositioningPillar(index: number): void {
    this.editPositioning?.messagingPillars.splice(index, 1);
  }

  addMarketingGoal(): void {
    this.editMarketingPlan?.goals.push({ title: '', measurable: '', timeBound: '' });
  }

  removeMarketingGoal(index: number): void {
    this.editMarketingPlan?.goals.splice(index, 1);
  }

  addMarketingKpi(): void {
    this.editMarketingPlan?.kpis.push({ name: '', metricKey: '', targetValue: '', unit: '' });
  }

  removeMarketingKpi(index: number): void {
    this.editMarketingPlan?.kpis.splice(index, 1);
  }

  addMarketingSegment(): void {
    this.editMarketingPlan?.segments.push({ name: '', description: '' });
  }

  removeMarketingSegment(index: number): void {
    this.editMarketingPlan?.segments.splice(index, 1);
  }

  addMarketingChannel(): void {
    this.editMarketingPlan?.channelMix.push({ channelKey: '', weight: null, rationale: '' });
  }

  removeMarketingChannel(index: number): void {
    this.editMarketingPlan?.channelMix.splice(index, 1);
  }

  addMarketingContentPillar(): void {
    this.editMarketingPlan?.contentPillars.push({ title: '', themesJoined: '' });
  }

  removeMarketingContentPillar(index: number): void {
    this.editMarketingPlan?.contentPillars.splice(index, 1);
  }

  addMarketingTimelineWeek(): void {
    this.editMarketingPlan?.timeline.push({ week: null, milestonesJoined: '' });
  }

  removeMarketingTimelineWeek(index: number): void {
    this.editMarketingPlan?.timeline.splice(index, 1);
  }

  canSave(): boolean {
    if (!this.model.title.trim()) return false;
    if (this.isRichView()) {
      return true;
    }
    if (this.isBusinessPlanType()) {
      return Boolean(this.model.objective.trim() && this.model.targetSegment.trim() && this.model.budgetRange.trim() && this.model.timeline.trim() && this.model.kpis.trim());
    }
    if (this.isMarketingPlanType()) {
      return Boolean(this.model.channels.trim() && this.model.campaigns.trim() && this.model.messaging.trim() && this.model.successMetrics.trim());
    }
    return true;
  }

  save(): void {
    const artifactJson = this.isRichView() ? this.buildRichArtifactJson() : this.buildArtifactJson();
    const citationRefsJson = this.isRichView()
      ? this.originalCitationRefsJson
      : '[]';
    const payload = {
      title: this.model.title.trim(),
      version: this.editingVersion,
      artifactJson,
      citationRefsJson
    };
    if (this.isNew()) {
      this.service.createStrategyArtifact({
        artifactType: this.model.artifactType,
        title: payload.title,
        version: '1.0.0',
        artifactJson: payload.artifactJson,
        citationRefsJson: payload.citationRefsJson
      }).subscribe(created => this.router.navigate(['/growth/agents/strategy-artifacts', created.id]));
      return;
    }
    const id = this.item()!.id;
    this.service.updateStrategyArtifact(id, payload).subscribe(() => this.reloadCurrent(id));
  }

  submit(): void {
    const id = this.item()?.id;
    if (!id) return;
    this.service.submitStrategyArtifact(id).subscribe(() => this.reloadCurrent(id));
  }

  approve(): void {
    const id = this.item()?.id;
    if (!id) return;
    this.service.approveStrategyArtifact(id).subscribe(() => this.reloadCurrent(id));
  }

  private reloadCurrent(id: string): void {
    this.service.listStrategyArtifacts().subscribe(rows => {
      this.items.set(rows);
      const current = rows.find(x => x.id === id);
      if (!current) return;
      this.hydrateArtifact(current);
    });
  }

  private hydrateArtifact(item: GrowthStrategyArtifact): void {
    this.item.set(item);
    this.editingVersion = item.version || '1.0.0';
    this.originalArtifactObj = parseStrategyArtifactJson(item.artifactJson);
    this.originalCitationRefsJson = item.citationRefsJson?.trim() ? item.citationRefsJson : '[]';
    this.model = this.fromArtifact(item);
    this.hydrateRichViews(item);
  }

  private hydrateRichViews(item: GrowthStrategyArtifact): void {
    const kind = detectRichStrategyArtifactKind(item.artifactJson, item.artifactType);
    const rich = kind !== null;
    this.richKind = kind;
    this.isRichView.set(rich);
    this.richSwot.set(kind === 'swot' ? parseRichSwotPayload(item.artifactJson) : null);
    this.hydrateRichEditModels(kind, item.artifactJson);
  }

  private hydrateRichEditModels(kind: RichStrategyArtifactKind | null, artifactJson: string): void {
    this.editSwot = null;
    this.editSituation = null;
    this.editPositioning = null;
    this.editMarketingPlan = null;
    if (!kind) {
      return;
    }

    switch (kind) {
      case 'swot':
        this.editSwot = hydrateEditableSwotModel(parseRichSwotPayload(artifactJson));
        break;
      case 'situationAssessment':
        this.editSituation = hydrateEditableSituationModel(parseRichSituationAssessmentPayload(artifactJson));
        break;
      case 'positioning':
        this.editPositioning = hydrateEditablePositioningModel(parseRichPositioningPayload(artifactJson));
        break;
      case 'marketingPlan':
        this.editMarketingPlan = hydrateEditableMarketingPlanModel(parseRichMarketingPlanPayload(artifactJson));
        break;
    }
  }

  private buildRichArtifactJson(): string {
    if (!this.richKind) {
      return JSON.stringify(this.originalArtifactObj ?? {});
    }

    return buildMergedRichArtifactJson(this.richKind, this.originalArtifactObj, {
      swot: this.editSwot,
      situation: this.editSituation,
      positioning: this.editPositioning,
      marketingPlan: this.editMarketingPlan
    });
  }

  private fromArtifact(item: GrowthStrategyArtifact): StrategyArtifactFormModel {
    const artifact = this.parseArtifactFields(item.artifactJson);
    const swot = this.parseSwotFields(item.artifactJson);
    return {
      artifactType: normalizeStrategyArtifactType(item.artifactType),
      title: item.title,
      objective: artifact.objective,
      targetSegment: artifact.targetSegment,
      budgetRange: artifact.budgetRange,
      timeline: artifact.timeline,
      kpis: artifact.kpis,
      channels: artifact.channels,
      campaigns: artifact.campaigns,
      messaging: artifact.messaging,
      successMetrics: artifact.successMetrics,
      linkedBusinessPlanId: artifact.businessPlanId,
      strengths: swot.strengths,
      weaknesses: swot.weaknesses,
      opportunities: swot.opportunities,
      threats: swot.threats
    };
  }

  private emptyModel(): StrategyArtifactFormModel {
    return {
      artifactType: 'BusinessPlan',
      title: '',
      objective: '',
      targetSegment: '',
      budgetRange: '',
      timeline: '',
      kpis: '',
      channels: '',
      campaigns: '',
      messaging: '',
      successMetrics: '',
      linkedBusinessPlanId: null,
      strengths: '',
      weaknesses: '',
      opportunities: '',
      threats: ''
    };
  }

  private buildArtifactJson(): string {
    if (this.model.artifactType === 'BusinessPlan') {
      return JSON.stringify({
        objective: this.model.objective.trim(),
        targetSegment: this.model.targetSegment.trim(),
        budgetRange: this.model.budgetRange.trim(),
        timeline: this.model.timeline.trim(),
        kpis: this.model.kpis.trim()
      });
    }
    if (this.model.artifactType === 'MarketingPlan') {
      return JSON.stringify({
        businessPlanId: this.model.linkedBusinessPlanId,
        channels: this.model.channels.trim(),
        campaigns: this.model.campaigns.trim(),
        messaging: this.model.messaging.trim(),
        successMetrics: this.model.successMetrics.trim()
      });
    }
    return JSON.stringify({
      strengths: this.model.strengths.trim(),
      weaknesses: this.model.weaknesses.trim(),
      opportunities: this.model.opportunities.trim(),
      threats: this.model.threats.trim()
    });
  }

  private parseSwotFields(artifactJson: string): Pick<StrategyArtifactFormModel, 'strengths' | 'weaknesses' | 'opportunities' | 'threats'> {
    try {
      const parsed = JSON.parse(artifactJson) as Record<string, unknown>;
      return {
        strengths: typeof parsed['strengths'] === 'string' ? parsed['strengths'] : '',
        weaknesses: typeof parsed['weaknesses'] === 'string' ? parsed['weaknesses'] : '',
        opportunities: typeof parsed['opportunities'] === 'string' ? parsed['opportunities'] : '',
        threats: typeof parsed['threats'] === 'string' ? parsed['threats'] : ''
      };
    } catch {
      return { strengths: '', weaknesses: '', opportunities: '', threats: '' };
    }
  }

  private parseArtifactFields(artifactJson: string): {
    objective: string;
    targetSegment: string;
    budgetRange: string;
    timeline: string;
    kpis: string;
    channels: string;
    campaigns: string;
    messaging: string;
    successMetrics: string;
    businessPlanId: string | null;
  } {
    try {
      const parsed = JSON.parse(artifactJson) as Record<string, unknown>;
      return {
        objective: typeof parsed['objective'] === 'string' ? parsed['objective'] : '',
        targetSegment: typeof parsed['targetSegment'] === 'string' ? parsed['targetSegment'] : '',
        budgetRange: typeof parsed['budgetRange'] === 'string' ? parsed['budgetRange'] : '',
        timeline: typeof parsed['timeline'] === 'string' ? parsed['timeline'] : '',
        kpis: typeof parsed['kpis'] === 'string' ? parsed['kpis'] : '',
        channels: typeof parsed['channels'] === 'string' ? parsed['channels'] : '',
        campaigns: typeof parsed['campaigns'] === 'string' ? parsed['campaigns'] : '',
        messaging: typeof parsed['messaging'] === 'string' ? parsed['messaging'] : '',
        successMetrics: typeof parsed['successMetrics'] === 'string' ? parsed['successMetrics'] : '',
        businessPlanId: typeof parsed['businessPlanId'] === 'string' ? parsed['businessPlanId'] : null
      };
    } catch {
      return {
        objective: '',
        targetSegment: '',
        budgetRange: '',
        timeline: '',
        kpis: '',
        channels: '',
        campaigns: '',
        messaging: '',
        successMetrics: '',
        businessPlanId: null
      };
    }
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  tr(key: string, fallback: string): string {
    const translated = this.t(key);
    return translated === key ? fallback : translated;
  }
}
